import { HttpError } from '@/lib/https';

/** A form path the contact drawer can mark: a core field, or `customFields.<key>`. */
export type ContactFormErrorPath =
  | 'email'
  | 'phone'
  | 'firstName'
  | 'lastName'
  | `customFields.${string}`;

export type ContactApiFieldErrors = {
  /** Errors the form can show on the field itself, in the order the API listed them. */
  fieldErrors: { path: ContactFormErrorPath; message: string }[];
  /** Messages for fields the form does not render — for the toast. */
  unmapped: string[];
};

const CORE_FIELD_PATHS: Record<string, ContactFormErrorPath> = {
  email: 'email',
  phone: 'phone',
  firstname: 'firstName',
  lastname: 'lastName',
};

const ATTRIBUTE_PREFIX = 'attributes.';

const toFormPath = (
  field: string,
  customFieldKeys: ReadonlySet<string>
): ContactFormErrorPath | undefined => {
  const lower = field.toLowerCase();
  if (lower.startsWith(ATTRIBUTE_PREFIX)) {
    const key = field.slice(ATTRIBUTE_PREFIX.length);
    return customFieldKeys.has(key) ? `customFields.${key}` : undefined;
  }
  return CORE_FIELD_PATHS[lower];
};

/**
 * Reads a `400`'s `fieldErrors` — `{ "email": [...], "attributes.religion": [...] }` — into form
 * paths, so the drawer can mark the exact input instead of showing a generic "Validation failed".
 * Anything that is not an `HttpError` with field errors comes back empty.
 */
export const mapContactApiFieldErrors = (
  error: unknown,
  customFieldKeys: Iterable<string> = []
): ContactApiFieldErrors => {
  const result: ContactApiFieldErrors = { fieldErrors: [], unmapped: [] };
  if (!(error instanceof HttpError) || error.status !== 400) return result;

  const raw = (error.error as { fieldErrors?: unknown }).fieldErrors;
  if (!raw || typeof raw !== 'object') return result;

  const keys = new Set(customFieldKeys);
  for (const [field, messages] of Object.entries(raw as Record<string, unknown>)) {
    const message = Array.isArray(messages)
      ? messages.find((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
      : undefined;
    if (!message) continue;

    const path = toFormPath(field, keys);
    if (path) {
      result.fieldErrors.push({ path, message: message.trim() });
    } else {
      result.unmapped.push(message.trim());
    }
  }

  return result;
};
